<script lang="ts">
	import IconCaretDown from "@ishavskraft/shared/components/icons/IconCaretDown.svelte";
	import { selectAreas } from "#lib/utils/areaUtils.js";
	import type { Omraade } from "#lib/types/omraade.js";
	import type { DayView } from "#lib/utils/spotprisenViews.js";

	type Props = {
		/** Resolved area — URL-explicit or implicit default (selectAreas[0]). */
		currentArea: Omraade;
		/** Day-view slug — used only by the default href builder; ignored when
		 *  `hrefBuilder` is provided. Kept for the spotprisen call site. */
		view?: DayView;
		/** Optional custom href builder. Receives an area's seoSlug and
		 *  returns the full pathname. When set, takes precedence over the
		 *  `view`-based default so the component can be reused from other
		 *  spotprisen sub-routes (e.g. /spotprisen/historikk). */
		hrefBuilder?: (slug: string) => string;
		class?: string;
	};

	let { currentArea, view = null, hrefBuilder, class: classes = "" }: Props = $props();
	const areaHref = (slug: string) =>
		hrefBuilder ? hrefBuilder(slug) : ["/spotprisen", view, slug].filter(Boolean).join("/");
</script>

{#snippet areaLabel(area: Omraade)}
	<!-- Container-query driven: which span is visible depends on the
	     nearest CSS container ancestor's inline-size. -->
	<!-- 1) Narrowest: "NO4" small, "Nord" below it -->
	<span class="label-compact w-fit">
		<small class="block">{area.value}</small>
		{area.shortLabel}
	</span>

	<!-- 2) Mid: "NO4" small, "Nord-Norge" below it -->
	<span class="label-mid">
		<small class="block">{area.value}</small>
		{area.label}
	</span>

	<!-- 3) Roomy: "Nord-Norge / NO4" on one line -->
	<span class="label-roomy">
		{area.label} <small>/ {area.value}</small>
	</span>
{/snippet}

<details class="area-select {classes} group">
	<summary class="button min-w-0 button-link px-0 py-0 leading-tight text-sm select-none">
		<span
			class={[
				"button-like flex items-center shadow-[0_0_0_1px_var(--primary-strong)] py-1 pl-3 pr-2 min-h-[48px] rounded-full bg-white",
				"group-open:bg-glod group-open:text-natt group-open:shadow-none",
			]}
		>
			{#key currentArea.value}
				<span class="area-flash flex items-baseline gap-1">
					{currentArea.value}
					{currentArea.label}
				</span>
			{/key}
			<IconCaretDown class="caret shrink-0 mr-0.5 ml-2 select-none" />
		</span>
	</summary>

	<ul class="m-0 p-0 list-none flex gap-2 leading-[1.125]">
		{#each selectAreas as area (area.value)}
			{@const isCurrent = area.value === currentArea.value}
			<li>
				<a
					href={areaHref(area.seoSlug)}
					class={[
						"leading-[1.125] flex items-center button button-link border-0 rounded-0 p-1 min-s-[48px] active:no-underline relative w-full text-center text-sm",
						isCurrent && "current",
					]}
					aria-current={isCurrent ? "page" : undefined}
					data-sveltekit-reset="false"
					data-sveltekit-replacestate
				>
					{@render areaLabel(area)}
				</a>
			</li>
		{/each}
	</ul>
</details>

<style lang="postcss">
	ul {
		justify-content: space-between;
		container-type: inline-size; /* enable container queries */

		a.current {
			box-shadow: inset 0 -3px var(--primary-strong);
		}
	}

	summary.button {
		position: relative;
		z-index: 3;
	}

	details :global(.caret) {
		transform: translateY(0.1em);
	}

	details[open] {
		& ul {
			position: relative;
			padding-bottom: var(--s-3);
			padding-inline: 4px;

			& li {
				position: relative;
				z-index: 2;
			}

			&::after {
				content: "";
				position: absolute;
				inset: -0.5rem 0 0 0;
				border-radius: 1rem;
				box-shadow: inset 0 0 0 1px var(--primary-strong);
				background: var(--surface);
				z-index: 1;
			}
		}

		& :global(.caret) {
			transform: rotate(180deg);
		}
	}

	/* Fade + slide-in when the active area changes. The surrounding {#key}
	 *  block re-mounts this span on value change, restarting the keyframe.
	 *  `will-change` keeps the element on a compositor layer so the in/out
	 *  swap doesn't cause a subpixel snap (visible as a small vertical bump). */
	.area-flash {
		animation: area-flash 0.35s ease-out;
		will-change: transform, opacity;
	}
	@keyframes area-flash {
		from {
			transform: translateX(-8px);
			opacity: 0;
		}
		to {
			transform: translateX(0);
			opacity: 1;
		}
	}
	@media (prefers-reduced-motion: reduce) {
		.area-flash {
			animation: none;
		}
	}

	/* === Default (narrowest container): compact stacked label ===
	   `display: flex; flex-direction: column` cleanly stacks the NO-code
	   <small> on top of the short label. Avoids the block-in-inline
	   fragmentation that caused text in narrow summaries to break
	   character by character. */
	.label-compact {
		display: flex;
		flex-direction: column;
		align-items: center;
		line-height: 1.1;
	}
	.label-mid,
	.label-roomy {
		display: none;
	}

	/* === Mid: stacked "NO4 / Sørøst-Norge" ===
	   Kicks in when each container has enough room for the full region name. */
	@container (min-width: 35rem) {
		.label-compact {
			display: none;
		}
		.label-mid {
			display: flex;
			flex-direction: column;
			align-items: center;
			line-height: 1.1;
		}
	}
</style>
